<template>
    <b-spinner v-if="!model"></b-spinner>
    <div v-else class="fn-edit">
        <!-- Header -->
        <div class="fn-header">
            <div class="fn-header-left">
                <router-link to="/admin/functions" class="fn-back">
                    <b-icon icon="arrow-left-short" font-scale="1.4"></b-icon>
                </router-link>
                <h4 class="fn-title">{{ model.name }}</h4>
                <b-badge v-if="isReadOnly" variant="info" pill class="fn-readonly-badge">
                    <b-icon icon="lock-fill" font-scale="0.8" class="mr-1"></b-icon>read-only
                </b-badge>
            </div>
            <div class="fn-header-right">
                <transition name="fade">
                    <span v-if="isDirty" class="fn-unsaved"></span>
                </transition>
                <b-button variant="success" size="sm" @click="save()" class="fn-save-btn">
                    <b-icon icon="check2" class="mr-1"></b-icon>{{ $t('save') }}
                    <kbd class="fn-kbd">⌘S</kbd>
                </b-button>
                <protokol-settings-dropdown size="sm" v-if="isPlatformIntegrator">
                    <b-dropdown-item-button @click="remove">{{ $t('delete') }}</b-dropdown-item-button>
                </protokol-settings-dropdown>
            </div>
        </div>

        <!-- Version Pipeline -->
        <div class="fn-versions">
            <div class="fn-version-stage">
                <span class="fn-version-label">IDE</span>
                <b-form-select v-model="previewVersion" :options="availableVersion" size="sm" class="fn-version-select"></b-form-select>
                <b-button size="sm" variant="link" @click="forkModal = true" v-if="model.dev_version != ''" class="fn-version-action">
                    <b-icon icon="bezier2"></b-icon>
                </b-button>
                <b-button size="sm" variant="link" class="text-danger fn-version-action" @click="removeVersion()" v-if="model.dev_version != '' && canRemoveVersion">
                    <b-icon icon="trash"></b-icon>
                </b-button>
            </div>
            <div class="fn-version-promote">
                <b-icon icon="chevron-right" class="fn-version-arrow"></b-icon>
            </div>
            <div class="fn-version-stage">
                <span class="fn-version-label">Dev</span>
                <b-form-select v-model="devVersion" @change="publishDevVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="fn-version-select">
                    <template #first>
                        <b-form-select-option :value="null" disabled>{{ devVersion }}</b-form-select-option>
                    </template>
                </b-form-select>
                <span class="fn-version-chip" :class="model.dev_version ? 'fn-version-chip--success' : 'fn-version-chip--inactive'">
                    <b-icon :icon="model.dev_version ? 'check-circle-fill' : 'dash-circle'" class="fn-version-chip-icon"></b-icon>
                    {{ model.dev_version || $t('notActive') }}
                </span>
            </div>
            <div class="fn-version-promote">
                <b-icon icon="chevron-right" class="fn-version-arrow"></b-icon>
            </div>
            <div class="fn-version-stage">
                <span class="fn-version-label">Live</span>
                <b-form-select v-model="liveVersion" @change="publishLiveVersion($event)" :options="versionOptionsWithEmpty" size="sm" class="fn-version-select">
                    <template #first>
                        <b-form-select-option :value="null" disabled>{{ liveVersion }}</b-form-select-option>
                    </template>
                </b-form-select>
                <span class="fn-version-chip" :class="model.public_version ? 'fn-version-chip--live' : 'fn-version-chip--inactive'">
                    <b-icon :icon="model.public_version ? 'globe2' : 'dash-circle'" class="fn-version-chip-icon"></b-icon>
                    {{ model.public_version || $t('notPublished') }}
                </span>
            </div>
        </div>

        <!-- Main Content Tabs -->
        <b-tabs class="fn-tabs" nav-class="fn-tab-nav" content-class="fn-tab-body">
            <b-tab active>
                <template #title><b-icon icon="code-slash" class="mr-1"></b-icon>Code</template>
                <div class="fn-editor-wrap" :class="{ 'fn-editor-readonly': isReadOnly }">
                    <CodeEditor :withButton="false" v-model="expression"
                        :readOnly="isReadOnly"
                        :enableProtokolTypes="true"
                        :functionSignature="activeSignature"
                        :height="showTestPanel ? 420 : 650" />
                </div>
            </b-tab>
            <b-tab>
                <template #title><b-icon icon="braces" class="mr-1"></b-icon>{{ $t('signature.title') }}</template>
                <div class="fn-tab-pad">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <p class="text-muted mb-0">{{ $t('signature.description') }}</p>
                        <a href="https://docs.protokol.io/components/idl/#platform-function-signature" target="_blank" rel="noopener" class="fn-doc-link">
                            <b-icon icon="book" class="mr-1"></b-icon><small>{{ $t('documentation') }}</small>
                        </a>
                    </div>
                    <CodeEditor lang="application/json" :withButton="false" v-model="signatureJson" :height="500" />
                </div>
            </b-tab>
            <b-tab>
                <template #title><b-icon icon="key" class="mr-1"></b-icon>{{ $t('environment') }}</template>
                <div class="fn-tab-pad">
                    <div class="mb-3">
                        <b-button-group size="sm">
                            <b-button :variant="env === 'dev' ? 'primary' : 'outline-primary'" @click="env = 'dev'">Dev</b-button>
                            <b-button :variant="env === 'live' ? 'primary' : 'outline-primary'" @click="env = 'live'">Live</b-button>
                        </b-button-group>
                    </div>
                    <CodeEditor lang="application/json" :controls="false" v-model="envVariables" :withButton="false" :height="400" />
                </div>
            </b-tab>
            <b-tab>
                <template #title><b-icon icon="gear" class="mr-1"></b-icon>{{ $t('settings.title') }}</template>
                <div class="fn-tab-pad">
                    <div class="fn-setting">
                        <label class="fn-setting-label">{{ $t('tags') }}</label>
                        <v-select v-model.trim="model.tags" taggable multiple @input="formatTags" />
                        <small class="text-muted mt-1 d-block">{{ $t('tagsFunctionSettingTab') }}</small>
                    </div>
                    <div class="fn-setting">
                        <label class="fn-setting-label">{{ $t('public') }}</label>
                        <c-switch color="success" variant="pill" v-model="isPublic" :disabled="!isPlatformIntegrator"/>
                        <small class="text-muted mt-1 d-block">{{ $t('publicFunctionSettingTab') }}</small>
                    </div>
                    <div class="fn-setting" v-if="model.public">
                        <label class="fn-setting-label">{{ $t('apiUser') }}</label>
                        <v-select :options="apiUsers" v-model="model.config.api_user" :loading="!apiUsers" label="username" :reduce="o => o.uuid"/>
                        <small class="text-muted mt-1 d-block">{{ $t('apiUserFunctionSettingTab') }}</small>
                    </div>
                    <div class="fn-setting" v-if="model.public">
                        <label class="fn-setting-label">{{ $t('endpoint') }}</label>
                        <b-input-group size="sm">
                            <b-input-group-prepend>
                                <b-button-group size="sm">
                                    <b-button :variant="publicEndpointEnv === 'dev' ? 'primary' : 'outline-primary'" @click="updateEndpointEnv('dev')">dev</b-button>
                                    <b-button :variant="publicEndpointEnv === 'live' ? 'primary' : 'outline-primary'" @click="updateEndpointEnv('live')">live</b-button>
                                </b-button-group>
                            </b-input-group-prepend>
                            <b-form-input disabled v-if="publicEndpoint" v-model="publicEndpoint"/>
                        </b-input-group>
                    </div>
                </div>
            </b-tab>
        </b-tabs>

        <!-- Docked Test Panel -->
        <div class="fn-test" :class="{ 'fn-test--open': showTestPanel }">
            <div class="fn-test-bar" @click="toggleTestPanel">
                <div class="fn-test-bar-left">
                    <b-icon :icon="showTestPanel ? 'chevron-down' : 'chevron-up'" class="mr-2"></b-icon>
                    <span class="fn-test-bar-title">Console</span>
                    <b-badge v-if="testStatus" :variant="testStatusVariant" pill class="ml-2">
                        {{ testStatus }}
                    </b-badge>
                    <small v-if="testExecutionTime" class="text-muted ml-2">{{ testExecutionTime }}ms</small>
                </div>
                <div class="fn-test-bar-right" @click.stop>
                    <b-button size="sm" variant="success" @click="runTest" :disabled="testLoading">
                        <b-spinner small v-if="testLoading"></b-spinner>
                        <b-icon v-else icon="play-fill"></b-icon>
                        <span class="ml-1">Run</span>
                        <kbd class="fn-kbd ml-1">⌘↵</kbd>
                    </b-button>
                    <b-button size="sm" variant="outline-secondary" @click="clearTest" class="ml-1">
                        Clear
                    </b-button>
                </div>
            </div>
            <transition name="fn-slide">
                <div class="fn-test-body" v-show="showTestPanel">
                    <div class="fn-test-pane">
                        <div class="fn-test-pane-header">
                            <span>Input</span>
                            <b-button-group size="sm">
                                <b-button :variant="inputMode === 'simple' ? 'primary' : 'outline-primary'" @click="inputMode = 'simple'">
                                    {{ $t('simple') }}
                                </b-button>
                                <b-button :variant="inputMode === 'advanced' ? 'primary' : 'outline-primary'" @click="inputMode = 'advanced'">
                                    {{ $t('advanced') }}
                                </b-button>
                            </b-button-group>
                        </div>
                        <template v-if="inputMode === 'simple'">
                            <b-form-textarea
                                v-model="simpleTestInput"
                                rows="6"
                                :placeholder="$t('enterTestData')"
                                :state="simpleInputValid"
                                class="fn-mono"
                            ></b-form-textarea>
                            <b-form-invalid-feedback v-if="simpleInputValid === false">
                                {{ $t('invalidJsonFormat') }}
                            </b-form-invalid-feedback>
                        </template>
                        <template v-else>
                            <b-tabs small class="fn-test-inner-tabs">
                                <b-tab title="Body" active>
                                    <CodeEditor v-model="testInputDataBody" :options="{ mode: 'json', showFullscreenButton: false }" :withButton="false" :height="140"></CodeEditor>
                                </b-tab>
                                <b-tab title="Query">
                                    <CodeEditor v-model="testInputDataQuery" :options="{ mode: 'json', showFullscreenButton: false }" :withButton="false" :height="140"></CodeEditor>
                                </b-tab>
                                <b-tab title="Headers">
                                    <CodeEditor v-model="testInputDataHeaders" :options="{ mode: 'json', showFullscreenButton: false }" :withButton="false" :height="140"></CodeEditor>
                                </b-tab>
                            </b-tabs>
                        </template>
                    </div>
                    <div class="fn-test-divider"></div>
                    <div class="fn-test-pane">
                        <div class="fn-test-pane-header">
                            <span>Output</span>
                            <b-button v-if="testOutput" size="sm" variant="outline-secondary" @click="copyOutput">
                                <b-icon icon="clipboard"></b-icon>
                            </b-button>
                        </div>
                        <div v-if="testLoading" class="fn-test-empty">
                            <b-spinner small class="mr-2"></b-spinner>
                            <span>{{ $t('runningTest') }}...</span>
                        </div>
                        <div v-else-if="testOutput">
                            <CodeEditor :value="JSON.stringify(testOutput, null, 2)" :options="{ mode: 'json', readOnly: true }" :withButton="false" :height="180"></CodeEditor>
                        </div>
                        <div v-else class="fn-test-empty">
                            <b-icon icon="terminal" font-scale="1.2" class="mr-2"></b-icon>
                            <span>{{ $t('runTestToSeeOutput') }}</span>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <!-- Fork Dialog -->
        <main-dialog v-model="forkModal" :ok-title="$t('fork')" @ok="forkVersion">
            <b-form-group :label="$t('version')">
                <b-input v-model="previewVersion" disabled></b-input>
            </b-form-group>
            <b-form-group :label="$t('newVersion')">
                <b-input v-model="forkTo"></b-input>
            </b-form-group>
        </main-dialog>
    </div>
</template>

<script>
import CodeEditor from '@/components/form/Editor/Code'
import { get, set, cloneDeep } from 'lodash'
import VSelect from 'vue-select'
import { Switch as cSwitch } from '@coreui/vue'

export default {
    name: 'FunctionEdit',
    components: {
        CodeEditor,
        VSelect,
        cSwitch
    },
    data() {
        return {
            model: null,
            ideVersion: this.$t('choose'),
            forkModal: false,
            showTestPanel: false,
            testLoading: false,
            testStatus: null,
            testExecutionTime: null,
            inputMode: 'simple',
            simpleTestInput: '',
            forkTo: null,
            env: 'dev',
            isDirty: false,
            modelLoaded: false,
            testInput: {
                body: {},
                query: {},
                headers: {}
            },
            testOutput: null,
            apiUsers: null,
            publicEndpoint: null,
            publicEndpointEnv: 'dev'
        }
    },
    computed: {
        availableVersion() {
            return this.model.settings.map(s => s.version)
        },
        versionOptionsWithEmpty() {
            return [
                { value: '', text: this.$t('deactivate') },
                ...this.availableVersion.map(v => ({ value: v, text: v }))
            ]
        },
        previewVersion: {
            get() {
                return this.ideVersion
            },
            set(val) {
                this.ideVersion = val
            }
        },
        liveVersion: {
            get() {
                return this.model.public_version || this.$t('notPublished')
            },
            set() {}
        },
        devVersion: {
            get() {
                return this.model.dev_version || this.$t('notActive')
            },
            set() {}
        },
        envVariables: {
            get() {
                return JSON.stringify(get(this.model.env_variables, this.env, {}) || {}, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                set(this.model, `env_variables.${this.env}`, vars)
            }
        },
        testInputDataBody: {
            get() {
                return JSON.stringify(this.testInput.body, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                this.testInput.body = vars
            }
        },
        testInputDataQuery: {
            get() {
                return JSON.stringify(this.testInput.query, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                this.testInput.query = vars
            }
        },
        testInputDataHeaders: {
            get() {
                return JSON.stringify(this.testInput.headers, null, 4)
            },
            set(vars) {
                vars = JSON.parse(vars)
                this.testInput.headers = vars
            }
        },
        expression: {
            get() {
                let settings = this.model.settings.find(s => s.version == this.ideVersion)
                return get(settings, 'expr', null)
            },
            set(val) {
                let settings = this.model.settings.find(s => s.version == this.ideVersion)
                settings.expr = val
            }
        },
        signatureJson: {
            get() {
                const s = this.model.settings.find(s => s.version == this.ideVersion)
                return JSON.stringify(s && s.signature ? s.signature : { input: {}, output: null }, null, 2)
            },
            set(v) {
                const s = this.model.settings.find(s => s.version == this.ideVersion)
                if (s) {
                    try {
                        this.$set(s, 'signature', JSON.parse(v))
                    } catch (e) { /* ignore invalid JSON */ }
                }
            }
        },
        activeSignature() {
            const s = this.model.settings.find(s => s.version == this.ideVersion)
            return (s && s.signature) ? s.signature : null
        },
        isPublic: {
            get() {
                return this.model.public
            },
            set(val) {
                if (val) {
                    this.$http.get(`/v1/system/function/signature/${this.publicEndpointEnv}/${this.model.uuid}`).then(resp => {
                        const { data } = resp
                        this.publicEndpoint = data.signature
                    })
                }
                this.model.public = val
            }
        },
        isPlatformIntegrator() {
            return this.model.integrator == "platform"
        },
        canRemoveVersion() {
            return this.model.settings.length > 1
        },
        isReadOnly() {
            return this.previewVersion == this.model.public_version && !!this.model.public_version
        },
        testStatusVariant() {
            if (this.testStatus === 'success') return 'success'
            if (this.testStatus === 'error') return 'danger'
            return 'warning'
        },
        simpleInputValid() {
            if (!this.simpleTestInput) return null
            try {
                JSON.parse(this.simpleTestInput)
                return true
            } catch {
                return false
            }
        }
    },
    methods: {
        toggleTestPanel() {
            this.showTestPanel = !this.showTestPanel
        },
        removeVersion() {
            this.model.settings = this.model.settings.filter(s => s.version != this.ideVersion)
            this.save()
            this.previewVersion = this.devVersion
        },
        async publishLiveVersion(version) {
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return
            this.model.public_version = version
            this.save()
        },
        async publishDevVersion(version) {
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return
            this.model.dev_version = version
            this.save()
        },
        forkVersion() {
            let settings = this.model.settings.find(s => s.version == this.previewVersion)
            let settingsToFork = cloneDeep(settings)
            settingsToFork.version = this.forkTo
            this.model.settings.push(settingsToFork)
            this.save()
        },
        save() {
            if (this.model) {
                let popup = this.$loadingPopup()
                this.$functions.update(this.$route.params.id, this.model).then(() => {
                    this.isDirty = false
                }).finally(() => {
                    popup.hide()
                })
            }
        },
        async runTest() {
            let requestBody = this.testInput.body

            if (this.inputMode === 'simple' && this.simpleTestInput) {
                try {
                    requestBody = JSON.parse(this.simpleTestInput)
                    this.testInput.body = requestBody
                } catch (e) {
                    this.$bvToast.toast(this.$t('invalidJsonFormat'), {
                        title: 'Error',
                        variant: 'danger',
                        autoHideDelay: 3000
                    })
                    return
                }
            }

            this.showTestPanel = true
            this.testLoading = true
            this.testStatus = 'running'
            const startTime = performance.now()

            try {
                const { data } = await this.$http.post(`/v1/system/function/run/${this.model.name}`, requestBody, {
                    params: this.testInput.query,
                    headers: {
                        'X-Debug': true,
                        ...this.testInput.headers
                    }
                })

                this.testOutput = data
                this.testStatus = 'success'
                this.testExecutionTime = Math.round(performance.now() - startTime)
            } catch (err) {
                console.error(err)
                this.testOutput = (err.response && err.response.data) || { error: err.message }
                this.testStatus = 'error'
                this.testExecutionTime = Math.round(performance.now() - startTime)

                this.$bvToast.toast(this.$t('unexpectedError'), {
                    variant: 'danger',
                    title: this.$t('error')
                })
            } finally {
                this.testLoading = false
            }
        },
        clearTest() {
            this.simpleTestInput = ''
            this.testInput = {
                body: {},
                query: {},
                headers: {}
            }
            this.testOutput = null
            this.testStatus = null
            this.testExecutionTime = null
        },
        copyOutput() {
            if (this.testOutput) {
                navigator.clipboard.writeText(JSON.stringify(this.testOutput, null, 2)).then(() => {
                    this.$bvToast.toast(this.$t('copiedToClipboard'), {
                        title: this.$t('success'),
                        variant: 'success',
                        autoHideDelay: 2000
                    })
                })
            }
        },
        async remove() {
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return
            this.$functions.delete(this.$route.params.id).then(() => {
                this.$router.push('/admin/functions')
            }).catch((err) => {
                console.error(err)
                this.$bvToast.toast(this.$t('unexpectedError'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            })
        },
        validateTag(tag) {
            const camelCaseRegex = /^[a-z][a-zA-Z0-9]*$/;
            const snakeCaseRegex = /^[a-z][a-z0-9_]*$/;
            return camelCaseRegex.test(tag) || snakeCaseRegex.test(tag);
        },
        formatTags() {
            this.model.tags = this.model.tags.map(tag => tag.trim().replaceAll(" ", "_"))
        },
        updateEndpointEnv(env) {
            this.publicEndpointEnv = env
            this.$http.get(`/v1/system/function/signature/${this.publicEndpointEnv}/${this.model.uuid}`).then(resp => {
                const { data } = resp
                this.publicEndpoint = data.signature
            })
        },
        handleKeydown(e) {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault()
                this.save()
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                if (!this.testLoading) this.runTest()
            }
        }
    },
    mounted() {
        this.$functions.get(this.$route.params.id).then(resp => {
            this.model = resp.data
            this.ideVersion = this.model.dev_version || (this.availableVersion.length > 0 ? this.availableVersion[0] : this.$t('choose'))
            this.$nextTick(() => {
                this.modelLoaded = true
            })
        }).catch(err => {
            this.$debugLog(err)
            this.$mainDialog.msgBoxOk('Failed to fetch model.')
        })

        this.$project.apiUsers().then(resp => {
            this.apiUsers = resp.data
        })

        document.addEventListener('keydown', this.handleKeydown)

        // ── Kortex AI tools ──────────────────────────────────────
        if (!window.kortex) return

        window.kortex.showHelper()
        window.kortex.registerContext(
            'Function Editor',
            'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
            'The user is editing a platform function. You can read the function code, signature, and metadata; ' +
            'modify the code; save changes; publish dev/live versions; and run tests with input data. ' +
            'Use get_function_info first to see what you are working with, then help the user write, debug, or refactor their function code.',
            'fa-code'
        )

        var self = this

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'get_function_info',
                    description: 'Returns metadata about the function being edited: name, UUID, available versions, public status, tags, and the current signature.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                var m = self.model
                if (!m) return { error: 'Function not loaded yet' }
                var s = m.settings.find(function (sv) { return sv.version === self.ideVersion })
                return {
                    uuid: m.uuid,
                    name: m.name,
                    integrator: m.integrator,
                    is_public: m.public || false,
                    tags: m.tags || [],
                    current_version: self.ideVersion,
                    dev_version: m.dev_version || null,
                    live_version: m.public_version || null,
                    available_versions: (m.settings || []).map(function (sv) { return sv.version }),
                    signature: s && s.signature ? s.signature : { input: {}, output: null },
                    expression_length: s && s.expr ? s.expr.length : 0
                }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'get_function_code',
                    description: 'Returns the full source code (expression) of the currently selected version of the function.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                var code = self.expression
                if (code === null || code === undefined) return { error: 'No code available for version: ' + self.ideVersion }
                return {
                    version: self.ideVersion,
                    code: code,
                    length: code.length
                }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'set_function_code',
                    description: 'Replaces the entire source code of the currently selected version. The user must save to persist.',
                    parameters: {
                        type: 'object',
                        required: ['code'],
                        properties: {
                            code: { type: 'string', description: 'The new function source code.' }
                        }
                    }
                }
            },
            function (args) {
                var code = args.code
                if (typeof code !== 'string') return { error: 'code must be a string.' }
                self.expression = code
                return { version: self.ideVersion, length: code.length, saved: false, note: 'Code updated in editor. User must click Save to persist.' }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'save_function',
                    description: 'Persists the current function code, signature, and all settings to the server.',
                    parameters: { type: 'object', properties: {} }
                }
            },
            function () {
                self.save()
                return { saved: true, note: 'Save request sent.' }
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'run_function_test',
                    description: 'Executes the function with the given input body and returns the result. Useful for debugging and verifying behavior.',
                    parameters: {
                        type: 'object',
                        properties: {
                            input: { type: 'object', description: 'The JSON input body to pass to the function.' }
                        }
                    }
                }
            },
            function (args) {
                var input = args.input || {}
                return self.$http.post('/v1/system/function/run/' + self.model.name, input, {
                    params: {},
                    headers: { 'X-Debug': true }
                }).then(function (resp) {
                    return { success: true, output: resp.data }
                }).catch(function (err) {
                    return { success: false, error: (err.response && err.response.data) || err.message }
                })
            }
        )

        window.kortex.registerTool(
            {
                type: 'function',
                function: {
                    name: 'publish_function_version',
                    description: 'Sets the dev or live version of the function to the specified version tag. Asks for confirmation before publishing live.',
                    parameters: {
                        type: 'object',
                        required: ['version', 'type'],
                        properties: {
                            version: { type: 'string', description: 'The version tag to publish (e.g. "v1", "v2").' },
                            type: { type: 'string', enum: ['dev', 'live'], description: 'Which environment to publish to: "dev" or "live".' }
                        }
                    }
                }
            },
            function (args) {
                var version = args.version
                var type = args.type
                if (type !== 'dev' && type !== 'live') return { error: 'type must be "dev" or "live".' }
                var available = self.availableVersion
                if (available.indexOf(version) === -1) {
                    return { error: 'Version "' + version + '" not found. Available: ' + available.join(', ') }
                }
                if (type === 'dev') {
                    self.model.dev_version = version
                } else {
                    self.model.public_version = version
                }
                self.save()
                return { published: true, version: version, type: type, note: 'Version published and saved.' }
            }
        )
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown)

        if (!window.kortex) return
        window.kortex.unregisterContext()
        window.kortex.hideHelper()
        window.kortex.unregisterTool('get_function_info')
        window.kortex.unregisterTool('get_function_code')
        window.kortex.unregisterTool('set_function_code')
        window.kortex.unregisterTool('save_function')
        window.kortex.unregisterTool('run_function_test')
        window.kortex.unregisterTool('publish_function_version')
    },

    watch: {
        model: {
            deep: true,
            handler() {
                if (this.modelLoaded) this.isDirty = true
            }
        },
        inputMode(newMode) {
            if (newMode === 'advanced' && this.simpleTestInput && this.simpleInputValid) {
                try {
                    this.testInput.body = JSON.parse(this.simpleTestInput)
                } catch (e) {}
            } else if (newMode === 'simple' && this.testInput.body) {
                this.simpleTestInput = JSON.stringify(this.testInput.body, null, 2)
            }
        },
        simpleTestInput(newVal) {
            if (this.inputMode === 'simple' && this.simpleInputValid) {
                try {
                    this.testInput.body = JSON.parse(newVal)
                } catch (e) {}
            }
        }
    }
}
</script>

<style scoped>
/* ── Layout ─────────────────────────────────────── */
.fn-edit {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 120px);
}

/* ── Header ─────────────────────────────────────── */
.fn-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-default);
}

.fn-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.fn-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.fn-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm, 6px);
    color: var(--text-secondary);
    transition: all 0.15s;
}
.fn-back:hover {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    text-decoration: none;
}

.fn-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fn-readonly-badge {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.3px;
}

.fn-unsaved {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warning-start, #ed8936);
    display: inline-block;
    animation: fn-pulse 2s infinite;
    flex-shrink: 0;
}

@keyframes fn-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
}

.fn-save-btn {
    font-weight: 500;
}

.fn-kbd {
    display: inline-block;
    padding: 1px 4px;
    font-size: 10px;
    font-family: inherit;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 3px;
    opacity: 0.7;
    line-height: 1.4;
}

/* ── Version Pipeline ───────────────────────────── */
.fn-versions {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-default);
    overflow-x: auto;
    scrollbar-width: none;
}
.fn-versions::-webkit-scrollbar {
    display: none;
}

.fn-version-stage {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.fn-version-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-muted, #949ba4);
    white-space: nowrap;
}

.fn-version-select {
    width: auto;
    min-width: 72px;
    max-width: 110px;
    font-size: 12px;
    padding: 2px 24px 2px 8px;
    height: 28px;
}

.fn-version-action {
    padding: 2px 4px;
    color: var(--text-secondary);
    line-height: 1;
}

.fn-version-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: var(--radius-sm, 6px);
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1.3;
}

.fn-version-chip-icon {
    font-size: 14px;
}

.fn-version-chip--success {
    background: rgba(72, 187, 120, 0.12);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.25);
}

.fn-version-chip--live {
    background: rgba(108, 128, 255, 0.12);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.25);
}

.fn-version-chip--inactive {
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
    color: var(--text-muted, #949ba4);
    border: 1px solid var(--border-default);
}

.fn-version-promote {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.fn-version-arrow {
    color: var(--text-muted, #949ba4);
    font-size: 11px;
    opacity: 0.6;
}

/* ── Tabs ───────────────────────────────────────── */
.fn-tabs {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.fn-tabs >>> .fn-tab-nav {
    border-bottom: 1px solid var(--border-default);
    padding-top: 4px;
}

.fn-tabs >>> .fn-tab-nav .nav-link {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    padding: 8px 14px;
    border: none;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
}

.fn-tabs >>> .fn-tab-nav .nav-link:hover {
    color: var(--text-primary);
    border-bottom-color: var(--border-hover, rgba(102, 126, 234, 0.25));
}

.fn-tabs >>> .fn-tab-nav .nav-link.active {
    color: var(--primary-start, #6c80ff);
    border-bottom-color: var(--primary-start, #6c80ff);
    background: transparent;
}

.fn-tabs >>> .fn-tab-body {
    flex: 1;
}

.fn-tab-pad {
    padding: 16px 0;
}

/* ── Editor ─────────────────────────────────────── */
.fn-editor-wrap {
    padding-top: 4px;
    position: relative;
}

.fn-editor-readonly {
    opacity: 0.85;
}

.fn-editor-readonly::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    border: 2px solid var(--info-start, #4299e1);
    border-radius: var(--radius-sm, 6px);
    opacity: 0.2;
}

.fn-doc-link {
    color: var(--text-secondary);
    transition: color 0.15s;
}
.fn-doc-link:hover {
    color: var(--primary-start, #6c80ff);
    text-decoration: none;
}

/* ── Settings ───────────────────────────────────── */
.fn-setting {
    padding: 16px 0;
    border-bottom: 1px solid var(--border-default);
}
.fn-setting:last-child {
    border-bottom: none;
}

.fn-setting-label {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-primary);
    margin-bottom: 8px;
    display: block;
}

/* ── Test Panel ─────────────────────────────────── */
.fn-test {
    border-top: 2px solid var(--border-default);
    background: var(--bg-card);
    margin-top: auto;
    border-radius: 0;
    transition: border-color 0.2s;
}

.fn-test--open {
    border-top-color: var(--primary-start, #6c80ff);
}

.fn-test-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;
}

.fn-test-bar:hover {
    background: var(--bg-card-secondary);
}

.fn-test-bar-left {
    display: flex;
    align-items: center;
}

.fn-test-bar-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-primary);
}

.fn-test-bar-right {
    display: flex;
    align-items: center;
    gap: 4px;
}

.fn-test-body {
    display: flex;
    border-top: 1px solid var(--border-default);
    min-height: 200px;
}

.fn-test-pane {
    flex: 1;
    padding: 10px 14px;
    min-width: 0;
    overflow: auto;
}

.fn-test-divider {
    width: 1px;
    background: var(--border-default);
    flex-shrink: 0;
}

.fn-test-pane-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-muted, #949ba4);
}

.fn-test-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 28px;
    color: var(--text-muted);
    font-size: 13px;
}

.fn-mono {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
    font-size: 13px;
}

.fn-test-inner-tabs >>> .nav-tabs {
    border-bottom: 1px solid var(--border-default);
}

/* ── Transitions ────────────────────────────────── */
.fn-slide-enter-active,
.fn-slide-leave-active {
    transition: all 0.2s ease;
    max-height: 500px;
    overflow: hidden;
}
.fn-slide-enter,
.fn-slide-leave-to {
    max-height: 0;
    overflow: hidden;
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
